import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Bot, Search, Zap } from "lucide-react";
import { Input } from "../components/ui/input";

const TEAM_COLORS = {
  "Executive Orchestration": "#F5A524",
  "Cultural Intelligence": "#00F0FF",
  "Audience Research": "#17C964",
  "Writing": "#F5A524",
  "Creative Direction": "#F5A524",
  "Visual Generation": "#00F0FF",
  "Audio": "#17C964",
  "Video Editing": "#F5A524",
  "Publishing": "#F5A524",
  "Analytics": "#00F0FF",
  "Learning": "#17C964",
  "Compliance": "#F31260",
  "Business": "#F5A524",
};

export default function Agents() {
  const { data: agents = [] } = useQuery({
    queryKey: ["agents"],
    queryFn: () => api.get("/agents").then(r => r.data),
  });
  const [q, setQ] = useState("");
  const [team, setTeam] = useState("all");

  const teams = useMemo(() => {
    const g = {};
    agents.forEach(a => { g[a.team] = (g[a.team] || 0) + 1; });
    return g;
  }, [agents]);

  const filtered = agents.filter(a => {
    if (team !== "all" && a.team !== team) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return a.name.toLowerCase().includes(s) || a.purpose.toLowerCase().includes(s) || a.team.toLowerCase().includes(s);
  });

  return (
    <div className="min-h-screen">
      <div className="border-b border-[#141414] px-8 py-5 sticky top-0 bg-[#050505]/90 backdrop-blur-md z-20">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#52525B]">// Agent Registry</div>
        <h1 className="font-display font-bold text-2xl">{agents.length} specialist agents · {Object.keys(teams).length} teams</h1>
      </div>

      <div className="p-8 space-y-6">
        <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
          <div className="relative flex-1 max-w-lg">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
            <Input
              data-testid="agents-search"
              placeholder="Search by name, purpose, team..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-10 h-11 bg-[#0a0a0a] border-[#222] rounded-sm focus:border-[#F5A524] focus-visible:ring-0"
            />
          </div>
          <div className="flex gap-1 flex-wrap">
            <button data-testid="team-filter-all" onClick={() => setTeam("all")} className={`px-3 py-1.5 text-xs border rounded-sm transition-colors ${team === "all" ? "border-[#F5A524] bg-[#F5A524]/10 text-[#F5A524]" : "border-[#222] text-[#A1A1AA] hover:text-white hover:border-[#333]"}`}>
              All · {agents.length}
            </button>
            {Object.entries(teams).map(([t, n]) => (
              <button key={t} data-testid={`team-filter-${t.replace(/\s+/g, '-').toLowerCase()}`} onClick={() => setTeam(t)} className={`px-3 py-1.5 text-xs border rounded-sm transition-colors ${team === t ? "border-[#F5A524] bg-[#F5A524]/10 text-[#F5A524]" : "border-[#222] text-[#A1A1AA] hover:text-white hover:border-[#333]"}`}>
                {t} · {n}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-[#171717] border border-[#171717]">
          {filtered.map(a => {
            const color = TEAM_COLORS[a.team] || "#F5A524";
            return (
              <div key={a.id} data-testid={`agent-card-${a.id}`} className="bg-[#0a0a0a] p-5 hover:bg-[#0f0f0f] transition-colors duration-200 relative group">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 border border-[#222] bg-[#0f0f0f] flex items-center justify-center">
                      <Bot className="w-4 h-4" style={{ color }} strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="font-mono text-[9px] uppercase tracking-widest" style={{ color }}>{a.team}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={`w-1.5 h-1.5 rounded-full ${a.status === "active" ? "bg-[#F5A524] pulse-amber" : "bg-[#333]"}`} />
                    <span className="font-mono text-[9px] uppercase text-[#52525B]">{a.status}</span>
                  </div>
                </div>
                <div className="font-display font-bold text-sm mb-2">{a.name}</div>
                <p className="text-xs text-[#A1A1AA] leading-relaxed mb-4 line-clamp-3">{a.purpose}</p>
                <div className="flex flex-wrap gap-1 mb-4">
                  {a.skills.slice(0, 3).map(s => (
                    <span key={s} className="px-1.5 py-0.5 border border-[#1f1f1f] font-mono text-[9px] text-[#A1A1AA]">{s}</span>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-2 pt-4 border-t border-[#141414]">
                  <div><div className="font-mono text-[9px] uppercase text-[#52525B]">ok</div><div className="font-mono text-[11px] text-[#17C964]">{(a.success_rate * 100).toFixed(1)}%</div></div>
                  <div><div className="font-mono text-[9px] uppercase text-[#52525B]">p50</div><div className="font-mono text-[11px] text-[#00F0FF]">{a.avg_latency_ms}ms</div></div>
                  <div><div className="font-mono text-[9px] uppercase text-[#52525B]">runs</div><div className="font-mono text-[11px]">{a.total_runs.toLocaleString()}</div></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
